import { Center, Flex, Icon } from "@chakra-ui/react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { listingsModel } from "models/listings";
import { useParams } from "react-router-dom";
import EmptyRecord from "assets/icons/medium/empty-record.svg?react";
import { Empty } from "components/empty";
import {
  ResponsiveCards,
  type ResponsiveCard,
} from "components/responsive-cards";
import { ScreenWrapper } from "components/screen-wrapper";
import { StatsGrid, type Stat } from "components/stats-grid";
import { useIsAgencyFeaturePreferenceEnabled } from "hooks/use-is-agency-feature-preference-enabled";
import { useRegion } from "hooks/use-region";
import { VENDOR_PORTAL_PATHS } from "modules/vendor-portal/paths";
import { sortEventsMostRecentFirst } from "modules/vendor-portal/screens/events/utils";
import type { FeedbackLocationState } from "modules/vendor-portal/screens/feedback/feedback-screen";
import {
  FeedbackGroupByOption,
  hasNonZeroInterestLevel,
  sortFeedbackMostRecentFirst,
} from "modules/vendor-portal/screens/feedback/utils";
import { AgentUpdates } from "modules/vendor-portal/screens/overview/components/agent-updates";
import { VendorBannerImage } from "modules/vendor-portal/screens/overview/components/banner-image/vendor-banner-image";
import { RecentFeedback } from "modules/vendor-portal/screens/overview/components/feedback/recent-feedback";
import { ListingPerformance } from "modules/vendor-portal/screens/overview/components/listing-performance/listing-performance";
import { EventSummaryContainer } from "modules/vendor-portal/screens/overview/components/open-home-events/event-summary-container";
import { PriceIndication } from "modules/vendor-portal/screens/overview/components/price-indication/price-indication";
import { hasMarketingImages } from "modules/vendor-portal/utils/locked-routes-config";
import { countOffers } from "modules/vendor-portal/utils/offers";

const emptyScreen = (
  <Center h={"100%"}>
    <Empty
      icon={<Icon as={EmptyRecord} boxSize={"auto"} />}
      emptyMessage={"Nothing to see just yet!"}
      emptyDesc={
        "Once your property activity kicks off, you’ll see a summary of key updates and insights right here"
      }
    />
  </Center>
);

export function OverviewScreen() {
  const region = useRegion();
  const { propertyId } = useParams();
  const { data, isLoading } = useSuspenseQuery(
    listingsModel.queries.read(propertyId),
  );
  const isAgencyFeaturePreferenceEnabled =
    useIsAgencyFeaturePreferenceEnabled();

  const agentUpdates = sortFeedbackMostRecentFirst([
    ...(data?.agent_updates?.entries ?? []),
  ]).slice(0, 3);
  const recentFeedback = sortFeedbackMostRecentFirst([
    ...(data?.applicant_feedback?.entries ?? []),
  ]).slice(0, 3);

  const events = data?.events ?? [];

  const recentEvents = sortEventsMostRecentFirst(
    events.filter((event) => new Date(event.starts_at) <= new Date()),
  ).slice(0, 3);

  const upcomingEvents = events.filter(
    (event) => new Date(event.starts_at) > new Date(),
  );

  const summary = data?.summary;
  // "Total offers" must equal the Offers & Contracts "Offers" count. Compute it
  // from the same inputs via the shared `countOffers` helper — open offers
  // (published crm_offers excluding accepted/unconditional active contracts)
  // plus offer-status feedback with a price indication — so the summary and the
  // detail view stay equal by construction, for listings with and without
  // active contracts. See CRM-13933.
  const totalOffers = countOffers(
    data?.offers,
    data?.applicant_feedback?.entries,
  );
  const priceAdvertiseAs = data?.price_advertise_as || "No price";
  const priceIndications = summary?.list_of_price_indications?.value || [];
  const shouldShowOpenHomeData = data?.category?.id !== "commercial";
  const inspectionsHeld =
    summary?.count_of_inspections_and_open_homes?.value || 0;

  const hasMarketingData = hasMarketingImages(data);
  // Only show the interest-level performance card when at least one non-zero
  // interest-level value exists — shares the same helper as the Feedback screen
  // and route-lock so the Overview doesn't render an empty pie chart. CRM-14119.
  const hasInterestLevelData = hasNonZeroInterestLevel(
    summary?.count_of_interested_groups_by_interest_level?.value,
  );

  const cards: ResponsiveCard[] = [
    agentUpdates.length > 0 && {
      label: "Agents Update",
      element: <AgentUpdates isLoading={isLoading} updates={agentUpdates} />,
    },
    recentFeedback.length > 0 && {
      label: "Recent Feedback",
      element: (
        <RecentFeedback isLoading={isLoading} feedback={recentFeedback} />
      ),
    },
    recentEvents.length > 0 && {
      label: "Recent Events",
      width: "50%",
      element: (
        <EventSummaryContainer
          title={"Recent Events"}
          isLoading={isLoading}
          events={recentEvents}
        />
      ),
    },
    upcomingEvents.length > 0 && {
      label: "Upcoming Events",
      width: "50%",
      element: (
        <EventSummaryContainer
          title={"Upcoming Events"}
          isLoading={isLoading}
          events={upcomingEvents}
        />
      ),
    },
    summary &&
      isAgencyFeaturePreferenceEnabled(
        "vendor_portal.functionality.interest_level.show_interest_level_data",
      ) &&
      hasInterestLevelData && {
        label: "Performance",
        width: "50%",
        element: <ListingPerformance isLoading={isLoading} summary={summary} />,
      },
    priceIndications.length > 0 &&
      isAgencyFeaturePreferenceEnabled(
        "vendor_portal.functionality.graphs.show_price_indicator_chart",
      ) && {
        label: "Price Indication",
        width: "50%",
        element: (
          <PriceIndication
            priceAdvertiseAs={priceAdvertiseAs}
            priceIndications={priceIndications}
            isLoading={isLoading}
          />
        ),
      },
  ].filter(Boolean) as ResponsiveCard[];

  const summaryStats: Stat[] = summary
    ? [
        {
          id: "days_since_listing",
          label: "Days since listing",
          value: summary.days_since_listed?.value || 0,
        },
        {
          id: "total_enquiries",
          label: "Total enquiries",
          value: summary.count_of_unique_people_who_have_enquired?.value || 0,
          navigateTo: {
            to: VENDOR_PORTAL_PATHS.FEEDBACK,
            navigateOptions: {
              state: {
                filters: ["enquiry"],
                groupBy: FeedbackGroupByOption.Name,
              } satisfies FeedbackLocationState,
            },
          },
        },
        {
          id: "inspections",
          label: `${region === "GBR" ? "Viewing" : "Inspection"}${
            inspectionsHeld !== 1 ? "s" : ""
          }${
            shouldShowOpenHomeData
              ? " / open home" + (inspectionsHeld !== 1 ? "s " : " ")
              : ""
          }${" held"}`,
          value: inspectionsHeld,
          navigateTo: {
            to: VENDOR_PORTAL_PATHS.EVENTS,
          },
        },
        {
          id: "total_attendees",
          label: "Total attendees",
          value: summary.count_of_unique_people_who_have_inspected?.value || 0,
          navigateTo: {
            to: VENDOR_PORTAL_PATHS.FEEDBACK,
            navigateOptions: {
              state: {
                filters: ["ofi", "inspection"],
                groupBy: FeedbackGroupByOption.Name,
              } satisfies FeedbackLocationState,
            },
          },
        },
        {
          id: "total_repeat_attendees",
          label: "Total repeat attendees",
          value:
            summary.count_of_unique_people_who_have_inspected_more_than_once
              ?.value || 0,
          navigateTo: {
            to: VENDOR_PORTAL_PATHS.FEEDBACK,
            navigateOptions: {
              state: {
                groupBy: FeedbackGroupByOption.Name,
                showOnly: {
                  key: "repeat_attendee",
                },
              } satisfies FeedbackLocationState,
            },
          },
        },
        ...(isAgencyFeaturePreferenceEnabled(
          "vendor_portal.navigation.show_offers",
        )
          ? [
              {
                id: "total_offers",
                label: "Total offers",
                value: totalOffers,
                navigateTo: {
                  to: VENDOR_PORTAL_PATHS.OFFERS,
                },
              },
            ]
          : []),
      ].filter((stat) => stat.value > 0) // Filter out stats with zero values
    : [];

  const hasSummaryStats = summaryStats.length > 0;
  const hasCards = cards.length > 0;

  if (!hasMarketingData) {
    return emptyScreen;
  }

  return (
    <ScreenWrapper>
      <VendorBannerImage isLoading={isLoading} listing={data} />
      {hasSummaryStats && hasCards ? (
        <Flex
          direction={"column"}
          px={{ base: "16px", md: "0px" }}
          rowGap={"32px"}
        >
          <StatsGrid stats={summaryStats} isLoading={isLoading} />
          <ResponsiveCards cards={cards} />
        </Flex>
      ) : (
        emptyScreen
      )}
    </ScreenWrapper>
  );
}
